export type SupportTicketPriority = 'P0' | 'P1' | 'P2' | 'P3'

export type SupportTicketStatus =
  | 'new'
  | 'in-progress'
  | 'waiting-on-reporter'
  | 'waiting-on-owner'
  | 'snoozed'
  | 'resolved'
  | 'dismissed'
  | 'merged'

export interface SupportTicketSlaState {
  slaDeadlineMs: number
  slaPausedDurationMs: number
  slaPausedAtMs: number | null
  slaBreached: boolean
}

/**
 * Adds Mon–Fri business days to a starting date, preserving time of day.
 * If starting on a weekend (Saturday or Sunday), advances to Monday first.
 */
export function addBusinessDays(startDate: Date | number, businessDays: number): Date {
  const current = new Date(startDate)
  let daysRemaining = Math.max(0, businessDays)

  const dayOfWeek = current.getDay() // 0 = Sunday, 6 = Saturday
  if (dayOfWeek === 6) {
    current.setDate(current.getDate() + 2)
  } else if (dayOfWeek === 0) {
    current.setDate(current.getDate() + 1)
  }

  while (daysRemaining > 0) {
    current.setDate(current.getDate() + 1)
    const day = current.getDay()
    if (day !== 0 && day !== 6) {
      daysRemaining -= 1
    }
  }

  return current
}

/**
 * Calculates the nominal SLA deadline (in ms) from creation timestamp and priority without pausing.
 * - P0: 1 hour
 * - P1: 4 hours
 * - P2: 24 hours
 * - P3: 5 business days
 */
export function calculateNominalDeadline(
  createdAt: Date | number,
  priority: SupportTicketPriority,
): number {
  const createdMs = typeof createdAt === 'number' ? createdAt : createdAt.getTime()

  switch (priority) {
    case 'P0':
      return createdMs + 1 * 60 * 60 * 1000
    case 'P1':
      return createdMs + 4 * 60 * 60 * 1000
    case 'P2':
      return createdMs + 24 * 60 * 60 * 1000
    case 'P3':
      return addBusinessDays(new Date(createdMs), 5).getTime()
    default:
      return createdMs + 24 * 60 * 60 * 1000
  }
}

/**
 * Computes the effective deadline by shifting the nominal deadline forward
 * by the accumulated paused duration.
 */
export function calculateEffectiveDeadline(
  createdAt: Date | number,
  priority: SupportTicketPriority,
  slaPausedDurationMs: number = 0,
): number {
  const nominal = calculateNominalDeadline(createdAt, priority)
  return nominal + Math.max(0, slaPausedDurationMs)
}

/**
 * Calculates remaining SLA in milliseconds.
 * If currently paused (slaPausedAtMs is set), countdown is frozen at that timestamp.
 */
export function calculateRemainingSlaMs(
  effectiveDeadlineMs: number,
  slaPausedAtMs: number | null = null,
  nowMs: number = Date.now(),
): number {
  const referenceTime = slaPausedAtMs !== null ? slaPausedAtMs : nowMs
  return effectiveDeadlineMs - referenceTime
}

/**
 * Checks whether an SLA is breached.
 * Tickets that are resolved, dismissed, or merged are never actively breaching.
 * Paused tickets (waiting-on-reporter) only breach if they had already breached before pausing.
 */
export function isSlaBreached(
  effectiveDeadlineMs: number,
  slaPausedAtMs: number | null = null,
  status: SupportTicketStatus = 'new',
  nowMs: number = Date.now(),
): boolean {
  if (status === 'resolved' || status === 'dismissed' || status === 'merged') {
    return false
  }
  const remaining = calculateRemainingSlaMs(effectiveDeadlineMs, slaPausedAtMs, nowMs)
  return remaining < 0
}

/**
 * Transitions SLA state across status and priority changes.
 * Handles pausing on 'waiting-on-reporter' and resuming when exiting that status.
 */
export function transitionTicketSla(
  currentSla: SupportTicketSlaState,
  createdAt: Date | number,
  priority: SupportTicketPriority,
  oldStatus: SupportTicketStatus,
  newStatus: SupportTicketStatus,
  nowMs: number = Date.now(),
): SupportTicketSlaState {
  let pausedDuration = currentSla.slaPausedDurationMs || 0
  let pausedAt = currentSla.slaPausedAtMs

  // 1. Pausing: Entering 'waiting-on-reporter' from another status
  if (newStatus === 'waiting-on-reporter' && oldStatus !== 'waiting-on-reporter') {
    pausedAt = nowMs
  }

  // 2. Resuming: Leaving 'waiting-on-reporter' to any other status
  if (oldStatus === 'waiting-on-reporter' && newStatus !== 'waiting-on-reporter') {
    if (pausedAt !== null) {
      const delta = Math.max(0, nowMs - pausedAt)
      pausedDuration += delta
      pausedAt = null
    }
  }

  // 3. Recompute effective deadline
  const effectiveDeadline = calculateEffectiveDeadline(createdAt, priority, pausedDuration)

  // 4. Determine breach state
  const breached = isSlaBreached(effectiveDeadline, pausedAt, newStatus, nowMs)

  return {
    slaDeadlineMs: effectiveDeadline,
    slaPausedDurationMs: pausedDuration,
    slaPausedAtMs: pausedAt,
    slaBreached: breached,
  }
}

/**
 * Formats remaining SLA milliseconds into human-readable string (e.g., '4h 12m', 'breached').
 */
export function formatSlaRemaining(remainingMs: number): { label: string; isBreached: boolean } {
  if (remainingMs < 0) {
    const overdue = Math.abs(remainingMs)
    const hours = Math.floor(overdue / (60 * 60 * 1000))
    const mins = Math.floor((overdue % (60 * 60 * 1000)) / (60 * 1000))
    if (hours > 24) {
      const days = Math.floor(hours / 24)
      return { label: `${days}d overdue`, isBreached: true }
    }
    return { label: hours > 0 ? `${hours}h ${mins}m overdue` : `${mins}m overdue`, isBreached: true }
  }

  const hours = Math.floor(remainingMs / (60 * 60 * 1000))
  const mins = Math.floor((remainingMs % (60 * 60 * 1000)) / (60 * 1000))

  if (hours > 24) {
    const days = Math.floor(hours / 24)
    return { label: `${days}d left`, isBreached: false }
  }
  if (hours > 0) {
    return { label: `${hours}h ${mins}m`, isBreached: false }
  }
  return { label: `${mins}m`, isBreached: false }
}
